import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Skeleton,
  TextField,
  MenuItem,
  darken,
  lighten,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  Checkbox,
  FormControlLabel,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Chart } from 'react-google-charts';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Colors } from 'src/shared/components/styles/colors';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import numberWithSpaces from 'src/shared/functions/numberWithSpaces';
import { ArrowForward, KeyboardArrowDown } from '@mui/icons-material';
import { TrendBadge } from 'src/modules/dashboard/v2/widgets/periodHelpers';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { InvoiceGroups } from 'src/shared/components/form/invoiceGroups';
import { useActionRequiredCounts } from 'src/modules/invoice/hooks/useActionRequiredCounts';
import { InvoiceMissionTypeTextEnum } from 'src/shared/models/enums/invoiceMissionTypeText.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import {
  getInvoiceRevenueByType,
  getInvoiceRevenueByMonth,
  getDeliveryMethodStats,
  getPaymentMethodStats,
  InvoiceRevenueByType,
  InvoiceRevenueByMonth,
  DeliveryMethodStats,
  PaymentMethodStats,
} from 'src/shared/services/invoiceService';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { invoiceTabs } from 'src/modules/invoice/components/invoiceTabs';

/* eslint-disable camelcase */

// ─── Period presets ─────────────────────────────────────────
type PeriodKey = 'thisYear' | 'thisMonth' | 'previousMonth' | 'previousYear' | 'custom';
type ComparePeriodKey = 'previousPeriod' | 'previousYear' | 'custom';

const getPeriodDates = (key: PeriodKey): { from: Dayjs; to: Dayjs } => {
  const now = dayjs();
  switch (key) {
    case 'thisYear':
      return { from: now.startOf('year'), to: now };
    case 'thisMonth':
      return { from: now.startOf('month'), to: now };
    case 'previousMonth':
      return { from: now.subtract(1, 'month').startOf('month'), to: now.subtract(1, 'month').endOf('month') };
    case 'previousYear':
      return { from: now.subtract(1, 'year').startOf('year'), to: now.subtract(1, 'year').endOf('year') };
    case 'custom':
    default:
      return { from: now.startOf('year'), to: now };
  }
};

const getComparePeriodDates = (key: ComparePeriodKey, mainFrom: Dayjs, mainTo: Dayjs): { from: Dayjs; to: Dayjs } => {
  switch (key) {
    case 'previousPeriod': {
      const days = mainTo.diff(mainFrom, 'day') + 1;
      return { from: mainFrom.subtract(days, 'day'), to: mainFrom.subtract(1, 'day') };
    }
    case 'previousYear':
      return { from: mainFrom.subtract(1, 'year'), to: mainTo.subtract(1, 'year') };
    case 'custom':
    default:
      return { from: mainFrom.subtract(1, 'year'), to: mainTo.subtract(1, 'year') };
  }
};

// ─── Per-card period config types ───────────────────────────
type PeriodConfig = { key: PeriodKey; customFrom: Dayjs; customTo: Dayjs };
type CompareConfig = { key: ComparePeriodKey; customFrom: Dayjs; customTo: Dayjs };

const defaultPeriod = (): PeriodConfig => ({ key: 'thisYear', customFrom: dayjs().startOf('year'), customTo: dayjs() });
const defaultCompare = (): CompareConfig => ({
  key: 'previousYear',
  customFrom: dayjs().subtract(1, 'year').startOf('year'),
  customTo: dayjs().subtract(1, 'year'),
});

type StoredPeriods = {
  pie: { key: PeriodKey; customFrom: string; customTo: string };
  pieCompare: { key: ComparePeriodKey; customFrom: string; customTo: string };
  chart: { key: PeriodKey; customFrom: string; customTo: string };
  chartCompare: { key: ComparePeriodKey; customFrom: string; customTo: string };
  delivery: { key: PeriodKey; customFrom: string; customTo: string };
  payment: { key: PeriodKey; customFrom: string; customTo: string };
};

const serializePeriod = (cfg: PeriodConfig) => ({
  key: cfg.key,
  customFrom: cfg.customFrom.format('YYYY-MM-DD'),
  customTo: cfg.customTo.format('YYYY-MM-DD'),
});
const serializeCompare = (cfg: CompareConfig) => ({
  key: cfg.key,
  customFrom: cfg.customFrom.format('YYYY-MM-DD'),
  customTo: cfg.customTo.format('YYYY-MM-DD'),
});
const deserializePeriod = (s: StoredPeriods['pie']): PeriodConfig => ({ key: s.key, customFrom: dayjs(s.customFrom), customTo: dayjs(s.customTo) });
const deserializeCompare = (s: StoredPeriods['pieCompare']): CompareConfig => ({
  key: s.key,
  customFrom: dayjs(s.customFrom),
  customTo: dayjs(s.customTo),
});

const loadStoredPeriods = (): Partial<{
  pie: PeriodConfig;
  pieCompare: CompareConfig;
  chart: PeriodConfig;
  chartCompare: CompareConfig;
  delivery: PeriodConfig;
  payment: PeriodConfig;
}> => {
  try {
    const raw = localStorage.getItem(LocalStorageKeys.InvoiceDashboardPeriods);
    if (!raw) return {};
    const stored: StoredPeriods = JSON.parse(raw);
    return {
      pie: deserializePeriod(stored.pie),
      pieCompare: deserializeCompare(stored.pieCompare),
      chart: deserializePeriod(stored.chart),
      chartCompare: deserializeCompare(stored.chartCompare),
      delivery: deserializePeriod(stored.delivery),
      payment: deserializePeriod(stored.payment),
    };
  } catch {
    return {};
  }
};

const resolvePeriod = (cfg: PeriodConfig) => (cfg.key === 'custom' ? { from: cfg.customFrom, to: cfg.customTo } : getPeriodDates(cfg.key));
const resolveCompare = (cfg: CompareConfig, mainFrom: Dayjs, mainTo: Dayjs) =>
  cfg.key === 'custom' ? { from: cfg.customFrom, to: cfg.customTo } : getComparePeriodDates(cfg.key, mainFrom, mainTo);

const formatDateRange = (from: Dayjs, to: Dayjs) => `${from.format('YYYY-MM-DD')} — ${to.format('YYYY-MM-DD')}`;

// ─── Reusable period selector popover ───────────────────────
type CardPeriodSelectorProps = {
  periodCfg: PeriodConfig;
  compareCfg?: CompareConfig;
  showCompare?: boolean;
  onConfirm: (period: PeriodConfig, compare?: CompareConfig) => void;
  onApplyAll: (period: PeriodConfig, compare?: CompareConfig) => void;
  periodOptions: { value: PeriodKey; label: string }[];
  comparePeriodOptions?: { value: ComparePeriodKey; label: string }[];
  t: (key: string) => string;
};

const CardPeriodSelector = ({
  periodCfg,
  compareCfg,
  showCompare,
  onConfirm,
  onApplyAll,
  periodOptions,
  comparePeriodOptions,
  t,
}: CardPeriodSelectorProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [pendingPeriod, setPendingPeriod] = useState<PeriodConfig>(periodCfg);
  const [pendingCompare, setPendingCompare] = useState<CompareConfig | undefined>(compareCfg);
  const [applyAll, setApplyAll] = useState(false);

  const period = resolvePeriod(periodCfg);
  const comparePeriod = compareCfg ? resolveCompare(compareCfg, period.from, period.to) : undefined;
  const periodLabel = periodOptions.find((o) => o.value === periodCfg.key)?.label ?? '';

  const handleOpen = () => {
    setPendingPeriod(periodCfg);
    setPendingCompare(compareCfg);
    setApplyAll(false);
    setOpen(true);
  };

  const handleConfirm = () => {
    onConfirm(pendingPeriod, pendingCompare);
    if (applyAll) onApplyAll(pendingPeriod, pendingCompare);
    setOpen(false);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          cursor: 'pointer',
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant='body2' fontWeight={500}>
            {periodLabel} · {formatDateRange(period.from, period.to)}
          </Typography>
          <KeyboardArrowDown sx={{ fontSize: 18 }} />
        </Box>
        {showCompare && comparePeriod && (
          <Typography variant='caption' color='text.disabled'>
            vs. {formatDateRange(comparePeriod.from, comparePeriod.to)}
          </Typography>
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 260 } } }}
      >
        <TextField
          select
          size='small'
          fullWidth
          label={t('invoice:dashboard.period.label')}
          value={pendingPeriod.key}
          onChange={(e) => setPendingPeriod({ ...pendingPeriod, key: e.target.value as PeriodKey })}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        >
          {periodOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        {pendingPeriod.key === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <DatePicker
              value={pendingPeriod.customFrom}
              format='YYYY-MM-DD'
              label={t('invoice:dashboard.period.from')}
              slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true } } }}
              onChange={(v) => v && setPendingPeriod({ ...pendingPeriod, customFrom: v })}
              slots={{ textField: TextField }}
            />
            <DatePicker
              value={pendingPeriod.customTo}
              format='YYYY-MM-DD'
              label={t('invoice:dashboard.period.to')}
              slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true } } }}
              onChange={(v) => v && setPendingPeriod({ ...pendingPeriod, customTo: v })}
              slots={{ textField: TextField }}
            />
          </Box>
        )}
        {showCompare && pendingCompare && comparePeriodOptions && (
          <>
            <TextField
              select
              size='small'
              fullWidth
              label={t('invoice:dashboard.comparePeriod.label')}
              value={pendingCompare.key}
              onChange={(e) => setPendingCompare({ ...pendingCompare!, key: e.target.value as ComparePeriodKey })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            >
              {comparePeriodOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            {pendingCompare.key === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <DatePicker
                  value={pendingCompare.customFrom}
                  format='YYYY-MM-DD'
                  label={t('invoice:dashboard.period.from')}
                  slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true } } }}
                  onChange={(v) => v && setPendingCompare({ ...pendingCompare!, customFrom: v })}
                  slots={{ textField: TextField }}
                />
                <DatePicker
                  value={pendingCompare.customTo}
                  format='YYYY-MM-DD'
                  label={t('invoice:dashboard.period.to')}
                  slotProps={{ textField: { size: 'small', InputLabelProps: { shrink: true } } }}
                  onChange={(v) => v && setPendingCompare({ ...pendingCompare!, customTo: v })}
                  slots={{ textField: TextField }}
                />
              </Box>
            )}
          </>
        )}
        <FormControlLabel
          control={<Checkbox size='small' checked={applyAll} onChange={(e) => setApplyAll(e.target.checked)} />}
          label={<Typography variant='body2'>{t('invoice:dashboard.period.applyToAll')}</Typography>}
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size='small' onClick={() => setOpen(false)}>
            {t('common:button.cancel')}
          </Button>
          <Button size='small' variant='contained' onClick={handleConfirm}>
            OK
          </Button>
        </Box>
      </Popover>
    </>
  );
};

const REVENUE_TYPE_KEYS = ['membershipFee', 'donation', 'event', 'other'] as const;

// ─── Main Page ──────────────────────────────────────────────
export const InvoiceDashboardPage = () => {
  const { t } = useTranslation(['invoice', 'dashboard']);
  const navigate = useNavigate();

  // Invoice group state
  const [invoiceGroupId, setInvoiceGroupId] = useState<number | undefined>(undefined);

  // Action required counts
  const { counts: actionRequired, isLoading: actionRequiredLoading } = useActionRequiredCounts(invoiceGroupId);

  // Period & compare period options
  const periodOptions: { value: PeriodKey; label: string }[] = [
    { value: 'thisYear', label: t('invoice:dashboard.period.thisYear') },
    { value: 'previousYear', label: t('invoice:dashboard.period.previousYear') },
    { value: 'thisMonth', label: t('invoice:dashboard.period.thisMonth') },
    { value: 'previousMonth', label: t('invoice:dashboard.period.previousMonth') },
    { value: 'custom', label: t('invoice:dashboard.period.custom') },
  ];

  const comparePeriodOptions: { value: ComparePeriodKey; label: string }[] = [
    { value: 'previousPeriod', label: t('invoice:dashboard.comparePeriod.previousPeriod') },
    { value: 'previousYear', label: t('invoice:dashboard.comparePeriod.previousYear') },
    { value: 'custom', label: t('invoice:dashboard.comparePeriod.custom') },
  ];

  // Per-card period state (restored from localStorage)
  const [storedPeriods] = useState(loadStoredPeriods);
  const [piePeriodCfg, setPiePeriodCfg] = useState<PeriodConfig>(storedPeriods.pie ?? defaultPeriod);
  const [pieCompareCfg, setPieCompareCfg] = useState<CompareConfig>(storedPeriods.pieCompare ?? defaultCompare);
  const [chartPeriodCfg, setChartPeriodCfg] = useState<PeriodConfig>(storedPeriods.chart ?? defaultPeriod);
  const [chartCompareCfg, setChartCompareCfg] = useState<CompareConfig>(storedPeriods.chartCompare ?? defaultCompare);
  const [deliveryPeriodCfg, setDeliveryPeriodCfg] = useState<PeriodConfig>(storedPeriods.delivery ?? defaultPeriod);
  const [paymentPeriodCfg, setPaymentPeriodCfg] = useState<PeriodConfig>(storedPeriods.payment ?? defaultPeriod);

  // Persist period configs to localStorage
  useEffect(() => {
    const data: StoredPeriods = {
      pie: serializePeriod(piePeriodCfg),
      pieCompare: serializeCompare(pieCompareCfg),
      chart: serializePeriod(chartPeriodCfg),
      chartCompare: serializeCompare(chartCompareCfg),
      delivery: serializePeriod(deliveryPeriodCfg),
      payment: serializePeriod(paymentPeriodCfg),
    };
    localStorage.setItem(LocalStorageKeys.InvoiceDashboardPeriods, JSON.stringify(data));
  }, [piePeriodCfg, pieCompareCfg, chartPeriodCfg, chartCompareCfg, deliveryPeriodCfg, paymentPeriodCfg]);

  // Resolved periods
  const piePeriod = useMemo(() => resolvePeriod(piePeriodCfg), [piePeriodCfg]);
  const pieComparePeriod = useMemo(() => resolveCompare(pieCompareCfg, piePeriod.from, piePeriod.to), [pieCompareCfg, piePeriod]);
  const chartPeriod = useMemo(() => resolvePeriod(chartPeriodCfg), [chartPeriodCfg]);
  const chartComparePeriod = useMemo(() => resolveCompare(chartCompareCfg, chartPeriod.from, chartPeriod.to), [chartCompareCfg, chartPeriod]);
  const deliveryPeriod = useMemo(() => resolvePeriod(deliveryPeriodCfg), [deliveryPeriodCfg]);
  const paymentPeriod = useMemo(() => resolvePeriod(paymentPeriodCfg), [paymentPeriodCfg]);

  // Apply period to all cards
  const handleApplyAll = useCallback((cfg: PeriodConfig, compare?: CompareConfig) => {
    setPiePeriodCfg(cfg);
    setChartPeriodCfg(cfg);
    setDeliveryPeriodCfg(cfg);
    setPaymentPeriodCfg(cfg);
    if (compare) {
      setPieCompareCfg(compare);
      setChartCompareCfg(compare);
    }
  }, []);

  // Revenue by type (pie chart) — real API
  const [revenueByType, setRevenueByType] = useState<InvoiceRevenueByType | null>(null);
  const [revenueByTypeLoading, setRevenueByTypeLoading] = useState(false);

  useEffect(() => {
    setRevenueByTypeLoading(true);
    getInvoiceRevenueByType(
      piePeriod.from.format('YYYY-MM-DD'),
      piePeriod.to.format('YYYY-MM-DD'),
      pieComparePeriod.from.format('YYYY-MM-DD'),
      pieComparePeriod.to.format('YYYY-MM-DD'),
      invoiceGroupId,
    )
      .then((res) => setRevenueByType(res.data))
      .catch(() => setRevenueByType(null))
      .finally(() => setRevenueByTypeLoading(false));
  }, [piePeriod.from, piePeriod.to, pieComparePeriod.from, pieComparePeriod.to, invoiceGroupId]);

  const revenueByTypeItems = useMemo(() => {
    if (!revenueByType) return [];
    return REVENUE_TYPE_KEYS.map((key) => ({
      type: key,
      amount: revenueByType[key] ?? 0,
      previousAmount: (revenueByType[`previous${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof InvoiceRevenueByType] as number) ?? 0,
    }));
  }, [revenueByType]);

  const revenueByTypeData: (string | number)[][] = [
    [t('invoice:dashboard.chart.revenueType'), t('invoice:dashboard.chart.amount')],
    ...revenueByTypeItems.map((r) => [t(`invoice:dashboard.kpi.${r.type}`), r.amount]),
  ];
  const totalPaid = revenueByTypeItems.reduce((sum, r) => sum + r.amount, 0);
  const previousTotalPaid = revenueByTypeItems.reduce((sum, r) => sum + r.previousAmount, 0);

  // Revenue by month (bar chart) — real API
  const [currentRevenueByMonth, setCurrentRevenueByMonth] = useState<InvoiceRevenueByMonth[]>([]);
  const [prevRevenueByMonth, setPrevRevenueByMonth] = useState<InvoiceRevenueByMonth[]>([]);
  const [revenueLoading, setRevenueLoading] = useState(false);

  useEffect(() => {
    setRevenueLoading(true);
    Promise.all([
      getInvoiceRevenueByMonth(chartPeriod.from.format('YYYY-MM-DD'), chartPeriod.to.format('YYYY-MM-DD'), invoiceGroupId),
      getInvoiceRevenueByMonth(chartComparePeriod.from.format('YYYY-MM-DD'), chartComparePeriod.to.format('YYYY-MM-DD'), invoiceGroupId),
    ])
      .then(([current, previous]) => {
        setCurrentRevenueByMonth(current.data);
        setPrevRevenueByMonth(previous.data);
      })
      .catch(() => {
        setCurrentRevenueByMonth([]);
        setPrevRevenueByMonth([]);
      })
      .finally(() => setRevenueLoading(false));
  }, [chartPeriod.from, chartPeriod.to, chartComparePeriod.from, chartComparePeriod.to, invoiceGroupId]);

  // Delivery method stats — real API (own period), filtered to member invoicing types
  const [deliveryMethodStats, setDeliveryMethodStats] = useState<DeliveryMethodStats[]>([]);
  const [deliveryMethodLoading, setDeliveryMethodLoading] = useState(false);
  const [deliverySendingType, setDeliverySendingType] = useState<string>('invoice');
  const MEMBER_MISSION_TYPES = useMemo(
    () => [InvoiceMissionTypeTextEnum.member, InvoiceMissionTypeTextEnum.autogiro, InvoiceMissionTypeTextEnum.einvoice],
    [],
  );

  useEffect(() => {
    setDeliveryMethodLoading(true);
    getDeliveryMethodStats(
      deliveryPeriod.from.format('YYYY-MM-DD'),
      deliveryPeriod.to.format('YYYY-MM-DD'),
      invoiceGroupId,
      MEMBER_MISSION_TYPES,
      deliverySendingType,
    )
      .then((res) => setDeliveryMethodStats(res.data))
      .catch(() => setDeliveryMethodStats([]))
      .finally(() => setDeliveryMethodLoading(false));
  }, [deliveryPeriod.from, deliveryPeriod.to, invoiceGroupId, MEMBER_MISSION_TYPES, deliverySendingType]);

  // Payment method stats — real API (own period)
  const [paymentMethodStats, setPaymentMethodStats] = useState<PaymentMethodStats[]>([]);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);

  useEffect(() => {
    setPaymentMethodLoading(true);
    getPaymentMethodStats(paymentPeriod.from.format('YYYY-MM-DD'), paymentPeriod.to.format('YYYY-MM-DD'), invoiceGroupId)
      .then((res) => setPaymentMethodStats(res.data))
      .catch(() => setPaymentMethodStats([]))
      .finally(() => setPaymentMethodLoading(false));
  }, [paymentPeriod.from, paymentPeriod.to, invoiceGroupId]);

  // Sort delivery methods by payment rate descending
  const HIDDEN_DELIVERY_METHODS = ['card', 'print'];
  const sortedDeliveryMethodStats = useMemo(
    () => [...deliveryMethodStats].filter((dm) => !HIDDEN_DELIVERY_METHODS.includes(dm.deliveryMethod)).sort((a, b) => b.paymentRate - a.paymentRate),
    [deliveryMethodStats],
  );

  const formatAmount = useCallback((amount: number) => `${numberWithSpaces(Math.round(amount))} kr`, []);

  // Monthly revenue chart data — stacked per uppdrag + jämförelseperiod som linje
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

  const MISSION_TYPE_COLORS: Record<string, string[]> = {
    member: ['#1565c0', '#42a5f5', '#90caf9', '#bbdefb'],
    sms: ['#2196f3'],
    einvoice: ['#64b5f6'],
    autogiro: ['#1e88e5'],
    stripe: ['#5c9eff'],
    gift: ['#2e7d32', '#66bb6a', '#a5d6a7'],
    gift_recurring: ['#43a047'],
    event: ['#e65100', '#fb8c00', '#ffb74d'],
  };
  const DEFAULT_COLORS = ['#78909c', '#b0bec5', '#cfd8dc'];

  const { monthlyChartData, chartColors, lineSeriesIndex, chartMax } = useMemo(() => {
    // Beräkna jämförelseperiodens månader baserat på vald period (inte API-svar som kan vara tomt)
    const compareMonths: string[] = [];
    let cMonth = chartComparePeriod.from.startOf('month');
    while (cMonth.isBefore(chartComparePeriod.to) || cMonth.isSame(chartComparePeriod.to, 'month')) {
      compareMonths.push(cMonth.format('YYYYMM'));
      cMonth = cMonth.add(1, 'month');
    }

    // Hitta unika månader och uppdrag
    const months = Array.from(new Set(currentRevenueByMonth.map((r) => r.yearMonth))).sort();
    const missions = Array.from(
      new Map(currentRevenueByMonth.map((r) => [r.invoiceMissionId, { id: r.invoiceMissionId, name: r.missionName, type: r.missionType }])).values(),
    );

    // Tilldela färger per uppdrag baserat på typ
    const typeCounters: Record<string, number> = {};
    const colors: string[] = missions.map((m) => {
      const palette = MISSION_TYPE_COLORS[m.type] || DEFAULT_COLORS;
      const idx = typeCounters[m.type] ?? 0;
      typeCounters[m.type] = idx + 1;
      return palette[idx % palette.length];
    });
    colors.push('#bdbdbd'); // jämförelseperiod

    // Bygg header: varje uppdrag med custom tooltip + annotation (total) + jämförelseperiod med tooltip
    const header: (string | number | object)[] = [
      t('invoice:dashboard.chart.month'),
      ...missions.flatMap((m) => [m.name || `Uppdrag ${m.id}`, { role: 'tooltip', type: 'string' }]),
      { role: 'annotation', type: 'string' },
      t('invoice:dashboard.chart.previousPeriod'),
      { role: 'tooltip', type: 'string' },
    ];

    // Bygg rader
    const formatTotal = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;
    const rows = months.map((ym, idx) => {
      const monthIdx = parseInt(ym.substring(4), 10) - 1;
      const label = monthNames[monthIdx] ?? ym;
      const valuesWithTooltips = missions.flatMap((m) => {
        const match = currentRevenueByMonth.find((r) => r.yearMonth === ym && r.invoiceMissionId === m.id);
        const amount = match?.amount ?? 0;
        return [amount, `${m.name || `Uppdrag ${m.id}`}: ${formatTotal(amount)}`];
      });
      const total = missions.reduce((sum, m) => {
        const match = currentRevenueByMonth.find((r) => r.yearMonth === ym && r.invoiceMissionId === m.id);
        return sum + (match?.amount ?? 0);
      }, 0);
      // Jämförelseperiod: matcha på ordinal position, använd compareMonths för korrekt label
      const prevYm = compareMonths[idx];
      const prevTotal = prevYm ? prevRevenueByMonth.filter((r) => r.yearMonth === prevYm).reduce((sum, r) => sum + r.amount, 0) : 0;
      const prevMonthIdx = prevYm ? parseInt(prevYm.substring(4), 10) - 1 : -1;
      const prevYear = prevYm ? prevYm.substring(0, 4) : '';
      const prevTooltip =
        prevMonthIdx >= 0
          ? `${monthNames[prevMonthIdx]} ${prevYear}: ${formatTotal(prevTotal)}`
          : `${t('invoice:dashboard.chart.previousPeriod')}: ${formatTotal(0)}`;
      return [label, ...valuesWithTooltips, formatTotal(total), prevTotal, prevTooltip];
    });

    // Beräkna max för y-axeln: högsta stapeltotal eller jämförelsevärde + 25% luft
    const maxBarTotal = rows.reduce((max, row) => {
      const barSum = missions.reduce((sum, _, mi) => sum + ((row[1 + mi * 2] as number) || 0), 0);
      const prevVal = (row[row.length - 2] as number) || 0;
      return Math.max(max, barSum, prevVal);
    }, 0);
    const vAxisMax = Math.ceil(maxBarTotal * 1.25);

    return { monthlyChartData: [header, ...rows], chartColors: colors, lineSeriesIndex: missions.length, chartMax: vAxisMax };
  }, [currentRevenueByMonth, prevRevenueByMonth, chartComparePeriod, t]);

  return (
    <GridWrapper>
      <Grid item xs>
        <MainContentHeader title={t('invoice:dashboard.header')} />
      </Grid>
      <Grid
        item
        xs='auto'
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          '& .MuiAutocomplete-inputRoot': { backgroundColor: 'white' },
        }}
      >
        <InvoiceGroups
          ownerId={invoiceGroupId}
          autoSelect
          placeholder={t('invoice:invoiceGroups')}
          callback={(val) => setInvoiceGroupId(val || undefined)}
        />
      </Grid>
      <Grid item xs={12} />

      {/* Action required */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1.5 }}>
            {t('invoice:dashboard.sections.actionRequired')}
          </Typography>
          {actionRequiredLoading ? (
            <Grid container spacing={1.5}>
              {[0, 1].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Skeleton variant='rectangular' height={52} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={1.5}>
              {(() => {
                const items = [
                  {
                    count: actionRequired.unmatchedBankPayments,
                    label: t('invoice:dashboard.actionRequired.unmatchedBankPayments'),
                    onClick: () => navigate(RoutesEnum.IncomingBankPayment),
                  },
                  {
                    count: actionRequired.unlinkedMandates,
                    label: t('invoice:dashboard.actionRequired.unlinkedMandates'),
                    onClick: () => navigate(RoutesEnum.Autogiro, { state: { filterLinked: 'unlinked' } }),
                  },
                  {
                    count: actionRequired.unaccountedPayments,
                    label: t('invoice:dashboard.actionRequired.unaccountedPayments'),
                    onClick: () => navigate(RoutesEnum.PaymentTransactions),
                  },
                  {
                    count: actionRequired.membersActionRequired,
                    label: t('invoice:dashboard.actionRequired.membersActionRequired'),
                    onClick: () => navigate(RoutesEnum.Memberautogirostatus, { state: { onlyBlocked: true } }),
                  },
                  {
                    count: actionRequired.donorsActionRequired,
                    label: t('invoice:dashboard.actionRequired.donorsActionRequired'),
                    onClick: () => navigate(RoutesEnum.DonorAutogiro, { state: { onlyBlocked: true } }),
                  },
                  {
                    count: actionRequired.eInvoiceApplications,
                    label: t('invoice:dashboard.actionRequired.eInvoiceApplications'),
                    onClick: () => navigate(RoutesEnum.EInvoiceApplications),
                  },
                ].filter((item) => item.count > 0);

                if (items.length === 0) {
                  return (
                    <Grid item xs={12}>
                      <Typography variant='body2' color='text.secondary'>
                        {t('invoice:dashboard.actionRequired.nothingToDo')}
                      </Typography>
                    </Grid>
                  );
                }

                return items.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Box
                      onClick={item.onClick}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 36,
                          height: 36,
                          borderRadius: '50%',
                          backgroundColor: (thm) => lighten(thm.palette.error.main, 0.8),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: (thm) => darken(thm.palette.error.main, 0.3) }}>
                          {item.count}
                        </Typography>
                      </Box>
                      <Typography variant='body2' fontWeight={500} sx={{ flex: 1 }}>
                        {item.label}
                      </Typography>
                      <ArrowForward sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </Box>
                  </Grid>
                ));
              })()}
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* Revenue by type */}
      <Grid item xs={12} md={6} lg={6}>
        <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant='h6' fontWeight={600}>
                {t('invoice:dashboard.sections.revenueByType')}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {t('invoice:dashboard.sections.revenueByTypeDesc')}
              </Typography>
            </Box>
            <CardPeriodSelector
              periodCfg={piePeriodCfg}
              compareCfg={pieCompareCfg}
              showCompare
              onConfirm={(p, c) => {
                setPiePeriodCfg(p);
                if (c) setPieCompareCfg(c);
              }}
              onApplyAll={handleApplyAll}
              periodOptions={periodOptions}
              comparePeriodOptions={comparePeriodOptions}
              t={t}
            />
          </Box>
          {(() => {
            if (revenueByTypeLoading) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Skeleton variant='circular' width={170} height={170} />
                  <Box sx={{ flex: 1 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} width='80%' height={28} sx={{ mb: 0.5 }} />
                    ))}
                  </Box>
                </Box>
              );
            }
            if (totalPaid === 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('invoice:dashboard.noData')}
                  </Typography>
                </Box>
              );
            }
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Chart
                    width='170px'
                    height='170px'
                    chartType='PieChart'
                    data={revenueByTypeData}
                    options={{
                      pieHole: 0.75,
                      is3D: false,
                      chartArea: { width: '92%', height: '92%' },
                      legend: 'none',
                      pieSliceText: 'none',
                      tooltip: { textStyle: { fontSize: 12, fontName: 'Source Sans 3' } },
                      colors: [
                        lighten(Colors.ChipColors.Blue, 0.4),
                        lighten(Colors.ChipColors.Green, 0.4),
                        lighten(Colors.Orange.Main, 0.4),
                        lighten(Colors.ChipColors.DustyBlue, 0.3),
                      ],
                      pieSliceBorderColor: 'transparent',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
                      {t('invoice:dashboard.kpi.totalPaid')}
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                      {formatAmount(totalPaid)}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                      <TrendBadge current={totalPaid} previous={previousTotalPaid} />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {revenueByTypeItems.map((r, i) => {
                    const colors = [
                      lighten(Colors.ChipColors.Blue, 0.4),
                      lighten(Colors.ChipColors.Green, 0.4),
                      lighten(Colors.Orange.Main, 0.4),
                      lighten(Colors.ChipColors.DustyBlue, 0.3),
                    ];
                    return (
                      <Box key={r.type}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors[i], flexShrink: 0 }} />
                          <Typography variant='body2' sx={{ minWidth: 120 }}>
                            {t(`invoice:dashboard.kpi.${r.type}`)}
                          </Typography>
                          <Typography variant='body2' fontWeight={600}>
                            {formatAmount(r.amount)}
                          </Typography>
                          {r.previousAmount !== 0 && (
                            <Tooltip title={`${t('invoice:dashboard.chart.previousPeriod')}: ${formatAmount(r.previousAmount)}`} arrow>
                              <Box sx={{ cursor: 'default' }}>
                                <TrendBadge current={r.amount} previous={r.previousAmount} />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, cursor: 'pointer' }}
            onClick={() => {
              localStorage.setItem(
                LocalStorageKeys.InvoiceSearchFilter,
                JSON.stringify({
                  status: invoiceTabs.find((x) => x.key === 'Paid'),
                  paidDateFrom: piePeriod.from.format('YYYY-MM-DD'),
                  paidDateTo: piePeriod.to.format('YYYY-MM-DD'),
                  searchString: '',
                  pageIndex: 0,
                  pageSize: 50,
                  ...(invoiceGroupId ? { ownerId: invoiceGroupId } : {}),
                }),
              );
              navigate(RoutesEnum.Invoices);
            }}
          >
            <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: Colors.ColorBox.Blue }}>
              {t('invoice:dashboard.insights.showInvoices')}
              <ArrowForward sx={{ fontSize: 16 }} />
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Monthly revenue bar chart */}
      <Grid item xs={12} md={6} lg={6}>
        <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant='h6' fontWeight={600}>
                {t('invoice:dashboard.sections.monthlyRevenue')}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Totalt inbetalt belopp per månad och uppdrag inom vald period
              </Typography>
            </Box>
            <CardPeriodSelector
              periodCfg={chartPeriodCfg}
              compareCfg={chartCompareCfg}
              showCompare
              onConfirm={(p, c) => {
                setChartPeriodCfg(p);
                if (c) setChartCompareCfg(c);
              }}
              onApplyAll={handleApplyAll}
              periodOptions={periodOptions}
              comparePeriodOptions={comparePeriodOptions}
              t={t}
            />
          </Box>
          {(() => {
            if (revenueLoading) {
              return <Skeleton variant='rectangular' animation='wave' sx={{ height: 200, borderRadius: 1 }} />;
            }
            if (currentRevenueByMonth.length === 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('invoice:dashboard.noData')}
                  </Typography>
                </Box>
              );
            }
            return (
              <Chart
                width='100%'
                height='220px'
                chartType='ComboChart'
                data={monthlyChartData}
                options={{
                  isStacked: true,
                  seriesType: 'bars',
                  series: {
                    [lineSeriesIndex]: { type: 'line', pointSize: 8, lineWidth: 2 },
                  },
                  chartArea: { width: '90%', height: '70%' },
                  colors: chartColors,
                  bar: { groupWidth: '70%' },
                  legend: { position: 'top', textStyle: { fontSize: 11, fontName: 'Source Sans 3' }, maxLines: 3 },
                  vAxis: { textPosition: 'none', viewWindow: { min: 0, max: chartMax || undefined } },
                  hAxis: { textStyle: { fontSize: 11, fontName: 'Source Sans 3' } },
                  tooltip: { textStyle: { fontSize: 12, fontName: 'Source Sans 3' } },
                  annotations: { textStyle: { fontSize: 11, fontName: 'Source Sans 3', color: '#000000', bold: true } },
                }}
              />
            );
          })()}
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, cursor: 'pointer' }}
            onClick={() => {
              localStorage.setItem(
                LocalStorageKeys.InvoiceSearchFilter,
                JSON.stringify({
                  status: invoiceTabs.find((x) => x.key === 'Paid'),
                  paidDateFrom: chartPeriod.from.format('YYYY-MM-DD'),
                  paidDateTo: chartPeriod.to.format('YYYY-MM-DD'),
                  searchString: '',
                  pageIndex: 0,
                  pageSize: 50,
                  ...(invoiceGroupId ? { ownerId: invoiceGroupId } : {}),
                }),
              );
              navigate(RoutesEnum.Invoices);
            }}
          >
            <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: Colors.ColorBox.Blue }}>
              {t('invoice:dashboard.insights.showInvoices')}
              <ArrowForward sx={{ fontSize: 16 }} />
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Recent missions – payment rate */}
      {/* Recent missions — commented out until real data is available
      <Grid item xs={12}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant='h6' fontWeight={600} sx={{ mb: 1.5 }}>
            {t('invoice:dashboard.sections.recentMissions')}
          </Typography>
          <Grid container spacing={2}>
            {MOCK_RECENT_MISSIONS.map((m) => {
              const pct = Math.round((m.paid / m.total) * 100);
              return (
                <Grid item xs={12} sm={4} key={m.name}>
                  <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: lighten(Colors.ChipColors.Blue, 0.92) }}>
                    <Typography variant='body2' fontWeight={600} noWrap>
                      {m.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <LinearProgress
                        variant='determinate'
                        value={pct}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: '#f6f6f6',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: pct >= 90 ? Colors.ChipColors.Green : pct >= 70 ? Colors.ChipColors.Yellow : Colors.ChipColors.Red,
                          },
                        }}
                      />
                      <Typography variant='body2' fontWeight={700} sx={{ minWidth: 40, textAlign: 'right' }}>
                        {pct}%
                      </Typography>
                    </Box>
                    <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
                      {m.paid} / {m.total} — {t('invoice:dashboard.insights.avgDays', { days: m.avgDays })}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Grid>
      */}

      {/* Delivery & payment method insights */}
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant='h6' fontWeight={600}>
              {t('invoice:dashboard.sections.deliveryMethodsMember')}
            </Typography>
            <CardPeriodSelector
              periodCfg={deliveryPeriodCfg}
              onConfirm={(p) => setDeliveryPeriodCfg(p)}
              onApplyAll={handleApplyAll}
              periodOptions={periodOptions}
              t={t}
            />
          </Box>
          <Tabs
            value={deliverySendingType}
            onChange={(_, val) => setDeliverySendingType(val)}
            sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' } }}
          >
            <Tab label={t('invoice:dashboard.sendingType.main')} value='invoice' />
            <Tab label={t('invoice:dashboard.sendingType.reminder1')} value='reminder1' />
            <Tab label={t('invoice:dashboard.sendingType.reminder2')} value='reminder2' />
          </Tabs>
          {(() => {
            if (deliveryMethodLoading) {
              return <Skeleton variant='rectangular' animation='wave' sx={{ height: 120, borderRadius: 1 }} />;
            }
            if (sortedDeliveryMethodStats.length === 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('invoice:dashboard.noData')}
                  </Typography>
                </Box>
              );
            }
            return (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('invoice:dashboard.insights.method')}</TableCell>
                    <TableCell align='right'>{t('invoice:dashboard.insights.invoiceCount')}</TableCell>
                    <TableCell align='right'>{t('invoice:dashboard.insights.paymentRate')}</TableCell>
                    <TableCell align='right'>{t('invoice:dashboard.insights.avgDaysCol')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDeliveryMethodStats.map((dm) => (
                    <TableRow
                      key={dm.deliveryMethod}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        const isGroupedType = dm.deliveryMethod === 'autogiro' || dm.deliveryMethod === 'electronicinvoice';
                        localStorage.setItem(
                          LocalStorageKeys.InvoiceSearchFilter,
                          JSON.stringify({
                            status: invoiceTabs.find((x) => x.key === 'All'),
                            deliveryMethod: dm.deliveryMethod,
                            missionType: isGroupedType
                              ? [dm.deliveryMethod === 'autogiro' ? InvoiceMissionTypeTextEnum.autogiro : InvoiceMissionTypeTextEnum.einvoice]
                              : [InvoiceMissionTypeTextEnum.member],
                            sendingType: {
                              invoice: InvoiceSendingTypeIntegerEnum.Main,
                              reminder1: InvoiceSendingTypeIntegerEnum.Reminder1,
                              reminder2: InvoiceSendingTypeIntegerEnum.Reminder2,
                            }[deliverySendingType],
                            createdDateFrom: deliveryPeriod.from.format('YYYY-MM-DD'),
                            createdDateTo: deliveryPeriod.to.format('YYYY-MM-DD'),
                            searchString: '',
                            pageIndex: 0,
                            pageSize: 50,
                          }),
                        );
                        navigate(RoutesEnum.Invoices);
                      }}
                    >
                      <TableCell>{t(`invoice:deliveryMethod.${dm.deliveryMethod}`, dm.deliveryMethod)}</TableCell>
                      <TableCell align='right'>{dm.totalInvoices}</TableCell>
                      <TableCell align='right'>
                        <CustomChip
                          label={`${Math.round(dm.paymentRate * 10) / 10}%`}
                          size='small'
                          color={(() => {
                            if (dm.paymentRate >= 85) return 'success';
                            if (dm.paymentRate >= 50) return 'warning';
                            return 'error';
                          })()}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        {Math.round(dm.avgDaysToPay)} {t('invoice:dashboard.insights.days')}
                      </TableCell>
                      <TableCell align='right'>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, color: Colors.ColorBox.Blue }}>
                          <Typography variant='body2' sx={{ fontSize: 13 }}>
                            {t('invoice:dashboard.insights.showInvoices')}
                          </Typography>
                          <ArrowForward sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </Paper>
      </Grid>

      {/* Payment method distribution */}
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant='h6' fontWeight={600}>
                {t('invoice:dashboard.sections.paymentMethods')}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {t('invoice:dashboard.sections.paymentMethodsDesc')}
              </Typography>
            </Box>
            <CardPeriodSelector
              periodCfg={paymentPeriodCfg}
              onConfirm={(p) => setPaymentPeriodCfg(p)}
              onApplyAll={handleApplyAll}
              periodOptions={periodOptions}
              t={t}
            />
          </Box>
          {(() => {
            if (paymentMethodLoading) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Skeleton variant='circular' width={170} height={170} />
                  <Box sx={{ flex: 1 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} width='80%' height={28} sx={{ mb: 0.5 }} />
                    ))}
                  </Box>
                </Box>
              );
            }
            if (paymentMethodStats.length === 0) {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('invoice:dashboard.noData')}
                  </Typography>
                </Box>
              );
            }
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Chart
                    width='170px'
                    height='170px'
                    chartType='PieChart'
                    data={[
                      [t('invoice:dashboard.insights.method'), t('invoice:dashboard.chart.amount')],
                      ...paymentMethodStats.map((pm) => [t(`invoice:dashboard.paymentSource.${pm.paymentMethod}`, pm.paymentMethod), pm.count]),
                    ]}
                    options={{
                      pieHole: 0.75,
                      is3D: false,
                      chartArea: { width: '92%', height: '92%' },
                      legend: 'none',
                      pieSliceText: 'none',
                      tooltip: { textStyle: { fontSize: 12, fontName: 'Source Sans 3' } },
                      colors: [
                        lighten(Colors.ChipColors.Blue, 0.5),
                        lighten(Colors.ChipColors.Green, 0.4),
                        lighten(Colors.Orange.Main, 0.4),
                        lighten(Colors.ChipColors.DustyBlue, 0.3),
                        lighten(Colors.ChipColors.Yellow, 0.3),
                        lighten(Colors.ChipColors.Red, 0.4),
                      ],
                      pieSliceBorderColor: 'transparent',
                      sliceVisibilityThreshold: 0,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>
                      {t('invoice:dashboard.insights.totalPayments')}
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                      {paymentMethodStats.reduce((sum, pm) => sum + pm.count, 0)} st
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {paymentMethodStats.map((pm, i) => {
                    const colors = [
                      lighten(Colors.ChipColors.Blue, 0.5),
                      lighten(Colors.ChipColors.Green, 0.4),
                      lighten(Colors.Orange.Main, 0.4),
                      lighten(Colors.ChipColors.DustyBlue, 0.3),
                      lighten(Colors.ChipColors.Yellow, 0.3),
                      lighten(Colors.ChipColors.Red, 0.4),
                    ];
                    return (
                      <Box key={pm.paymentMethod} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: colors[i % colors.length], flexShrink: 0 }} />
                        <Typography variant='body2'>{t(`invoice:dashboard.paymentSource.${pm.paymentMethod}`, pm.paymentMethod)}</Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {pm.count} st
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })()}
        </Paper>
      </Grid>

      {/* Failed payments, Overdue invoices, Incoming payments — commented out until real data is available */}
    </GridWrapper>
  );
};
